import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";
import { api } from "@/api/client";
import { fmtDateTime } from "@/utils/datetime";

export function NotificationBell() {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState<any[]>([]);
    const [unread, setUnread] = useState(0);
    const ref = useRef<HTMLDivElement>(null);

    const load = async () => {
        try {
            const { data } = await api.get("/notifications", { params: { limit: 20 } });
            setItems(data.items || []);
            setUnread(data.unread_count || 0);
        } catch { /* ignore — user might be pre-tenant */ }
    };
    useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

    useEffect(() => {
        const h = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
        window.addEventListener("mousedown", h);
        return () => window.removeEventListener("mousedown", h);
    }, []);

    const markRead = async (id: string) => {
        try { await api.post(`/notifications/${id}/read`); await load(); } catch { /* ignore */ }
    };
    const markAllRead = async () => {
        try { await api.post("/notifications/mark-all-read"); await load(); } catch { /* ignore */ }
    };

    return (
        <div ref={ref} className="relative">
            <button data-testid="notifications-btn" onClick={() => setOpen((v) => !v)} className="inline-flex items-center justify-center rounded-md border relative" style={{ width: 40, height: 40, color: "var(--text-muted)", background: "var(--input)", borderColor: "var(--border)" }} aria-label={t("topbar.notifications") as string}>
                <Bell size={18} />
                {unread > 0 && (
                    <span className="absolute -top-1 -right-1 rounded-full text-[10px] font-bold px-1.5 py-0.5 leading-none" style={{ background: "var(--error-fg)", color: "#fff", minWidth: 18, textAlign: "center" }} data-testid="notifications-badge">{unread}</span>
                )}
            </button>
            {open && (
                <div className="absolute right-0 top-11 z-40 rounded-md border shadow-xl w-[360px] max-w-[calc(100vw-32px)]" style={{ background: "var(--menu)", borderColor: "var(--border)" }} data-testid="notifications-menu">
                    <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: "var(--border)" }}>
                        <span className="text-sm font-medium">{t("notif.title")}</span>
                        {unread > 0 && <button data-testid="notifications-mark-all-read" onClick={markAllRead} className="text-xs" style={{ color: "var(--primary)" }}>{t("notif.mark_all_read")}</button>}
                    </div>
                    <div className="max-h-[420px] overflow-y-auto">
                        {items.length === 0 && <div className="p-4 text-sm" style={{ color: "var(--text-muted)" }}>{t("notif.empty")}</div>}
                        {items.map((n) => (
                            <button key={n.id} onClick={() => markRead(n.id)} className="w-full text-left px-3 py-2 border-b hover:bg-[var(--selected-bg)] transition-colors" style={{ borderColor: "var(--border)" }} data-testid={`notification-item-${n.id}`}>
                                <div className="flex items-start gap-2">
                                    {n.status === "unread" && <span className="mt-1.5 rounded-full shrink-0" style={{ width: 8, height: 8, background: "var(--primary)" }} />}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{String(t(n.title_key, n.params || {}))}</div>
                                        <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{String(t(n.body_key, n.params || {}))}</div>
                                        <div className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>{fmtDateTime(n.created_at)}</div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
